import KeenSlider from 'keen-slider'
import type { KeenSliderInstance, KeenSliderOptions } from 'keen-slider'
import 'keen-slider/keen-slider.min.css'

const DEFAULT_MARQUEE_SPEED_PX_PER_SEC = 60
const MARQUEE_DIRECTION = -1

export default class ImagesCarouselSection {
	private wrapper: HTMLElement
	private sliderRoot: HTMLElement | null = null
	private slider: KeenSliderInstance | null = null
	private marqueeRaf: number | null = null
	private lastMarqueeFrameTime = 0
	private autoplayEnabled = false
	private marqueeSpeedPxPerSec = DEFAULT_MARQUEE_SPEED_PX_PER_SEC

	constructor(wrapper: HTMLElement) {
		this.wrapper = wrapper
	}

	init() {
		this.sliderRoot = this.wrapper.querySelector<HTMLElement>('.hg-images-carousel__slider')
		if (!this.sliderRoot) return

		const slideCount = this.sliderRoot.querySelectorAll('.keen-slider__slide').length
		if (slideCount < 2) return

		const loopEnabled = this.wrapper.dataset.loop !== '0'
		this.autoplayEnabled = this.wrapper.dataset.autoplay !== '0'
		this.marqueeSpeedPxPerSec = this.parseMarqueeSpeedPxPerSec(this.wrapper.dataset.marqueeSpeed)

		this.slider = new KeenSlider(this.sliderRoot, this.getOptions(slideCount, loopEnabled))

		if (this.shouldRunMarquee()) {
			this.startMarquee()
		}

		window.addEventListener('resize', this.onResize)
		document.addEventListener('visibilitychange', this.onVisibilityChange)
	}

	cleanup() {
		window.removeEventListener('resize', this.onResize)
		document.removeEventListener('visibilitychange', this.onVisibilityChange)

		this.stopMarquee()

		this.slider?.destroy()
		this.slider = null
	}

	private shouldRunMarquee(): boolean {
		if (!this.autoplayEnabled) return false
		if (typeof window.matchMedia === 'function' &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			return false
		}
		return true
	}

	private onVisibilityChange = () => {
		if (document.hidden) {
			this.stopMarquee()
		} else if (this.shouldRunMarquee()) {
			this.startMarquee()
		}
	}

	private onResize = () => {
		if (!this.sliderRoot || !this.slider) return

		const slideCount = this.sliderRoot.querySelectorAll('.keen-slider__slide').length
		const loopEnabled = this.wrapper.dataset.loop !== '0'
		this.slider.update(this.getOptions(slideCount, loopEnabled))
	}

	private stopMarquee() {
		if (this.marqueeRaf !== null) {
			cancelAnimationFrame(this.marqueeRaf)
			this.marqueeRaf = null
		}
	}

	private startMarquee() {
		if (!this.shouldRunMarquee() || !this.slider) return
		this.stopMarquee()
		this.lastMarqueeFrameTime = performance.now()

		const tick = (now: number) => {
			const instance = this.slider
			if (!instance) return

			const dt = Math.min(now - this.lastMarqueeFrameTime, 64)
			this.lastMarqueeFrameTime = now

			const size = instance.size
			if (size > 0) {
				const delta = (this.marqueeSpeedPxPerSec / size) * (dt / 1000)
				instance.track.add(delta * -MARQUEE_DIRECTION)
			}

			this.marqueeRaf = requestAnimationFrame(tick)
		}

		this.marqueeRaf = requestAnimationFrame(tick)
	}

	private getOptions(slideCount: number, loop: boolean): KeenSliderOptions {
		const spacing = this.getSpacer3()

		return {
			loop: loop && slideCount > 1,
			mode: 'free',
			rubberband: true,
			dragStarted: () => {
				this.stopMarquee()
			},
			dragEnded: () => {
				if (this.shouldRunMarquee()) {
					this.startMarquee()
				}
			},
			slides: {
				perView: 1.25,
				spacing,
			},
			breakpoints: {
				'(min-width: 768px)': {
					slides: {
						perView: 2,
						spacing,
					},
				},
				'(min-width: 1200px)': {
					slides: {
						perView: 3,
						spacing,
					},
				},
			},
		}
	}

	private getSpacer3(): number {
		const root = document.documentElement
		const rootStyles = getComputedStyle(root)
		const raw = rootStyles.getPropertyValue('--main-padding-x').trim()
		const parsed = this.parseCssLength(raw, rootStyles)

		if (parsed !== null) return parsed

		const probe = document.createElement('div')
		probe.style.position = 'absolute'
		probe.style.visibility = 'hidden'
		probe.style.pointerEvents = 'none'
		probe.style.width = 'var(--main-padding-x)'
		root.appendChild(probe)
		const width = parseFloat(getComputedStyle(probe).width)
		probe.remove()

		return Number.isFinite(width) ? width : 0
	}

	private parseMarqueeSpeedPxPerSec(raw: string | undefined): number {
		if (raw === undefined || raw === '') return DEFAULT_MARQUEE_SPEED_PX_PER_SEC
		const n = Number(raw)
		if (!Number.isFinite(n)) return DEFAULT_MARQUEE_SPEED_PX_PER_SEC
		return Math.min(500, Math.max(1, Math.round(n)))
	}

	private parseCssLength(value: string, rootStyles: CSSStyleDeclaration): number | null {
		if (!value) return null

		if (value.endsWith('px')) {
			const px = parseFloat(value)
			return Number.isFinite(px) ? px : null
		}

		if (value.endsWith('rem')) {
			const rem = parseFloat(value)
			const rootFontSize = parseFloat(rootStyles.fontSize)
			return Number.isFinite(rem) && Number.isFinite(rootFontSize) ? rem * rootFontSize : null
		}

		return null
	}
}
